import type { LiveEdge, SpecOp } from './diff-types.js';

export type EdgeOp =
  | { kind: 'delete'; edgeId: string }
  | { kind: 'create'; sourceNodeId: string; targetNodeId: string };

export type EdgeReconcileResult = {
  ops: EdgeOp[];
};

export function defaultEdgesNeedReconcile(ops: SpecOp[], edges: LiveEdge[] | undefined): boolean {
  if (!Array.isArray(edges)) return false;

  const total = ops.filter((op) => op.kind !== 'delete').length;
  const finalNodeIds: string[] = Array.from({ length: total });
  for (const op of ops) {
    if (op.kind === 'create') return true;
    if (op.kind === 'update') finalNodeIds[op.specIndex] = op.nodeId;
  }

  if (finalNodeIds.some((id) => !id)) return true;
  return reconcileLinearEdges(finalNodeIds, edges).ops.length > 0;
}

/**
 * Given the final ordered list of node UUIDs (post-diff) and the current edges
 * on the live workflow, compute the minimal DELETE/POST ops to make the live
 * `default` edges form a linear chain through `finalNodeIds` in order.
 *
 * Non-default edges (e.g. condition `then`/`else`) are left alone -- the spec
 * compiler doesn't author them today, so we don't touch them.
 */
export function reconcileLinearEdges(
  finalNodeIds: string[],
  liveEdges: LiveEdge[]
): EdgeReconcileResult {
  const desired = new Set<string>();
  for (let i = 0; i < finalNodeIds.length - 1; i++) {
    desired.add(`${finalNodeIds[i]}→${finalNodeIds[i + 1]}`);
  }

  const ops: EdgeOp[] = [];
  const haveDefault = new Set<string>();

  for (const e of liveEdges) {
    if (e.edgeType !== 'default') continue;
    const key = `${e.sourceNodeId}→${e.targetNodeId}`;
    if (desired.has(key)) {
      haveDefault.add(key);
    } else {
      ops.push({ kind: 'delete', edgeId: e.id });
    }
  }

  for (const key of desired) {
    if (haveDefault.has(key)) continue;
    const [sourceNodeId, targetNodeId] = key.split('→');
    ops.push({ kind: 'create', sourceNodeId, targetNodeId });
  }

  return { ops };
}
